import React, { useEffect, useRef, useState } from "react";
import { prefersReducedMotion } from "../../../styles/particleField";

/** Last-resort art when a card has no image and the real one fails too. */
const FALLBACK_IMG = "https://images.pokemontcg.io/base1/1.png";

/**
 * CardImage — a card's art with an honest loading state.
 *
 * Card art is remote and slow, and the grid shows dozens at once. Without this
 * every tile flashed flat grey and then snapped to full art, which reads as the
 * page breaking rather than loading. A shimmer holds the space, then the art
 * cross-fades in.
 *
 * Images already in the browser cache decode before React attaches onLoad, so
 * the ref check below marks those complete immediately — otherwise cached
 * images would sit shimmering forever.
 */
const CardImage = ({
  src,
  alt,
  radius = 0,
  className,
  style,
  onLoad,
  // Told, with a boolean, whether the picture on screen is the FALLBACK rather
  // than `src`. The substitution itself is unchanged — this only makes it
  // OBSERVABLE, because `failed` below is private state and a parent making a
  // claim ABOUT `src` had no way to learn the claim had stopped being true.
  // #252: the card sheet's "Your photo" badge outlived the owner photo it
  // described and ended up labelling the publisher's stock art as the owner's
  // own photograph.
  //
  // A state notification and not a one-shot event, because the parent must not
  // keep its own copy of this: `failed` clears on a `src` change and on
  // remount, and a parent that only heard about failures would go on
  // suppressing a claim about a picture that had since come back. Fired from an
  // effect so it also reports `false` on mount and after a source change,
  // which is what keeps the two in step in that direction.
  //
  // Deliberately not named `onError`: an `onError` prop would land in
  // `imgProps`, and the spread below comes AFTER the img's own handler, so it
  // would replace the fallback logic rather than observe it.
  onFallbackChange,
  fallbackSrc = FALLBACK_IMG,
  ...imgProps
}) => {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const imgRef = useRef(null);
  const reduced = prefersReducedMotion();

  // Held in a ref so an inline arrow from the caller cannot make the
  // notification effect below depend on render identity.
  const onFallbackChangeRef = useRef(onFallbackChange);
  onFallbackChangeRef.current = onFallbackChange;

  // Reset when the source changes (grid recycling, opening a different card).
  useEffect(() => {
    setLoaded(false);
    setFailed(false);
  }, [src]);

  // Reports what the <img> is showing AT THIS COMMIT, which is why `src` is a
  // dependency too (the body reads it): on a source change this runs once
  // before the reset above has re-rendered, and at that commit the element
  // really does still hold the fallback. So the extra firing is redundant at
  // worst, never wrong. `!!src` because with no source at all the fallback is
  // not standing in for anything the caller named.
  useEffect(() => {
    onFallbackChangeRef.current?.(failed && !!src);
  }, [failed, src]);

  // Cached images can finish before this effect runs — no onLoad will fire.
  useEffect(() => {
    if (imgRef.current?.complete && imgRef.current.naturalWidth > 0) {
      setLoaded(true);
    }
  }, [src]);

  const resolvedSrc = failed ? fallbackSrc : src || fallbackSrc;

  return (
    <span
      data-testid="card-image"
      data-loaded={loaded ? "true" : "false"}
      style={{
        position: "relative",
        display: "block",
        width: "100%",
        height: "100%",
        overflow: "hidden",
        borderRadius: radius,
        background: "#eef2f7",
      }}
    >
      {!loaded && (
        <span
          data-testid="card-image-skeleton"
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(100deg, #eef2f7 30%, #f8fafc 50%, #eef2f7 70%)",
            backgroundSize: "220% 100%",
            animation: reduced ? undefined : "cardImageShimmer 1.25s ease-in-out infinite",
          }}
        />
      )}
      <img
        ref={imgRef}
        src={resolvedSrc}
        alt={alt}
        className={className}
        onLoad={(e) => {
          setLoaded(true);
          onLoad?.(e);
        }}
        onError={() => {
          // One retry against the fallback; if that fails too, stop shimmering
          // rather than trapping the tile in a loading state forever.
          // `onFallbackChange` is not called here: the notification effect
          // above is driven by `failed`, so it reports this the moment the
          // state lands — and, unlike a call from this handler, it also
          // reports the substitution going AWAY.
          if (!failed) setFailed(true);
          else setLoaded(true);
        }}
        style={{
          width: "100%",
          height: "100%",
          display: "block",
          opacity: loaded ? 1 : 0,
          transition: reduced ? undefined : "opacity 320ms ease",
          ...style,
        }}
        {...imgProps}
      />

      <style>{`
        @keyframes cardImageShimmer {
          from { background-position: 180% 0; }
          to   { background-position: -80% 0; }
        }
      `}</style>
    </span>
  );
};

export default CardImage;
